import React, { useEffect, useState, useRef } from "react";

const AnimatedBackground = () => {
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);

    const handleMouseMove = (e) => {
      if (!isMobile) {
        setMousePosition({
          x: (e.clientX / window.innerWidth) * 100,
          y: (e.clientY / window.innerHeight) * 100,
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', checkMobile);
    };
  }, [isMobile]);

  return (
    <div className="animated-bg-container fixed inset-0 overflow-hidden pointer-events-none z-0">
      {/* Subtle gradient that follows mouse - desktop only */}
      {!isMobile && (
        <div 
          className="absolute w-[500px] h-[500px] rounded-full opacity-15 transition-all duration-[2000ms] ease-out"
          style={{
            background: 'radial-gradient(circle, rgba(109, 180, 50, 0.25) 0%, transparent 70%)',
            left: `${mousePosition.x}%`,
            top: `${mousePosition.y}%`,
            transform: 'translate(-50%, -50%)',
          }}
        />
      )}

      {/* Floating Shapes - Fewer and smaller on mobile */}
      {!isMobile ? (
        <>
          {/* Desktop: More shapes */}
          <FloatingShape shape="hexagon" size={120} top="15%" left="10%" duration={25} delay={0} />
          <FloatingShape shape="hexagon" size={80} top="70%" left="85%" duration={30} delay={5} />
          <FloatingShape shape="hexagon" size={60} top="40%" left="75%" duration={20} delay={10} />
          <FloatingShape shape="circle" size={100} top="25%" left="80%" duration={22} delay={3} />
          <FloatingShape shape="circle" size={70} top="80%" left="20%" duration={28} delay={8} />
          <FloatingShape shape="circle" size={50} top="60%" left="50%" duration={18} delay={12} />
          <FloatingShape shape="square" size={90} top="50%" left="5%" duration={24} delay={2} />
          <FloatingShape shape="square" size={60} top="10%" left="60%" duration={26} delay={7} />
        </>
      ) : (
        <>
          {/* Mobile: Fewer, smaller shapes */}
          <FloatingShape shape="hexagon" size={60} top="10%" left="5%" duration={30} delay={0} />
          <FloatingShape shape="circle" size={40} top="30%" left="85%" duration={25} delay={5} />
          <FloatingShape shape="hexagon" size={50} top="70%" left="80%" duration={28} delay={8} />
          <FloatingShape shape="circle" size={35} top="85%" left="10%" duration={22} delay={3} />
        </>
      )}

      {/* Glowing Orbs - Adjusted for mobile */}
      <div 
        className="absolute rounded-full"
        style={{
          width: isMobile ? '150px' : '288px',
          height: isMobile ? '150px' : '288px',
          background: 'radial-gradient(circle, rgba(109, 180, 50, 0.12) 0%, transparent 70%)',
          top: '5%',
          right: isMobile ? '0%' : '10%',
          animation: 'gentlePulse 8s ease-in-out infinite',
        }}
      />
      <div 
        className="absolute rounded-full"
        style={{
          width: isMobile ? '120px' : '224px',
          height: isMobile ? '120px' : '224px',
          background: 'radial-gradient(circle, rgba(139, 192, 100, 0.1) 0%, transparent 70%)',
          bottom: '15%',
          left: isMobile ? '0%' : '5%',
          animation: 'gentlePulse 10s ease-in-out infinite',
          animationDelay: '3s',
        }}
      />

      {/* Animated gradient lines - Simpler on mobile */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute w-[200%] opacity-20"
          style={{
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, #6DB432 25%, #8BC064 50%, #6DB432 75%, transparent 100%)',
            top: '30%',
            left: '-50%',
            animation: 'slideRight 15s linear infinite',
          }}
        />
        {!isMobile && (
          <div 
            className="absolute w-[200%] opacity-15"
            style={{
              height: '1px',
              background: 'linear-gradient(90deg, transparent 0%, #8BC064 25%, #6DB432 50%, #8BC064 75%, transparent 100%)',
              top: '70%',
              left: '-50%',
              animation: 'slideRight 20s linear infinite reverse',
            }}
          />
        )}
      </div>

      {/* Subtle grid pattern - Larger on mobile for performance */}
      <div 
        className="absolute inset-0"
        style={{
          opacity: isMobile ? 0.015 : 0.025,
          backgroundImage: `
            linear-gradient(#6DB432 1px, transparent 1px),
            linear-gradient(90deg, #6DB432 1px, transparent 1px)
          `,
          backgroundSize: isMobile ? '100px 100px' : '80px 80px',
        }}
      />

      {/* Corner decorations - Smaller on mobile */}
      <div 
        className="absolute top-0 left-0 border-l border-t border-[#6DB432]/20 rounded-tl-3xl"
        style={{
          width: isMobile ? '60px' : '160px',
          height: isMobile ? '60px' : '160px',
        }}
      />
      <div 
        className="absolute bottom-0 right-0 border-r border-b border-[#6DB432]/20 rounded-br-3xl"
        style={{
          width: isMobile ? '60px' : '160px',
          height: isMobile ? '60px' : '160px',
        }}
      />
      {!isMobile && (
        <>
          <div className="absolute top-0 right-0 w-24 h-24 border-r border-t border-[#6DB432]/10 rounded-tr-2xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 border-l border-b border-[#6DB432]/10 rounded-bl-2xl" />
        </>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes gentlePulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.03); }
        }
        @keyframes slideRight {
          0% { transform: translateX(0); }
          100% { transform: translateX(50%); }
        }
        @keyframes floatSmooth {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(8px, -12px) rotate(3deg); }
          50% { transform: translate(-4px, -20px) rotate(-2deg); }
          75% { transform: translate(-10px, -8px) rotate(2deg); }
        }
        @keyframes floatSmoothMobile {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(0, -10px); }
        }
        @keyframes rotateShape {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Floating Shape Component
const FloatingShape = ({ shape, size, top, left, duration, delay }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getShapeStyle = () => {
    const baseStyle = {
      position: 'absolute',
      top,
      left,
      width: size,
      height: size,
      opacity: isMobile ? 0.12 : 0.15,
      animation: isMobile 
        ? `floatSmoothMobile ${duration}s ease-in-out infinite`
        : `floatSmooth ${duration}s ease-in-out infinite`,
      animationDelay: `${delay}s`,
      willChange: 'transform',
    };

    switch (shape) {
      case 'hexagon':
        return {
          ...baseStyle,
          clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
          background: 'linear-gradient(135deg, rgba(109, 180, 50, 0.3) 0%, rgba(139, 192, 100, 0.2) 100%)',
        };
      case 'circle':
        return {
          ...baseStyle,
          borderRadius: '50%',
          border: '1.5px solid rgba(109, 180, 50, 0.4)',
          background: 'radial-gradient(circle, rgba(109, 180, 50, 0.05) 0%, transparent 70%)',
        };
      case 'square':
        return {
          ...baseStyle,
          borderRadius: '12px',
          border: '1.5px solid rgba(139, 192, 100, 0.35)',
          background: 'transparent',
          animation: isMobile
            ? `floatSmoothMobile ${duration}s ease-in-out infinite`
            : `floatSmooth ${duration}s ease-in-out infinite, rotateShape ${duration * 2}s linear infinite`,
        };
      default:
        return baseStyle;
    }
  };

  return <div style={getShapeStyle()} />;
};

export default AnimatedBackground;
